export const EASTERN_TIME_ZONE = "America/New_York";

type EasternParts = { year: number; month: number; day: number; hour: number; minute: number; second: number };

function partsAt(instant: Date): EasternParts {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: EASTERN_TIME_ZONE,
    hourCycle: "h23",
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  }).formatToParts(instant);
  const number = (type: string) => Number(parts.find(part => part.type === type)?.value ?? NaN);
  return { year: number("year"), month: number("month"), day: number("day"), hour: number("hour"), minute: number("minute"), second: number("second") };
}

const pad = (value: number) => String(value).padStart(2, "0");
const key = (parts: EasternParts) => `${parts.year}-${pad(parts.month)}-${pad(parts.day)}T${pad(parts.hour)}:${pad(parts.minute)}`;

/** A browser-zone-independent value for <input type="datetime-local">. */
export function instantToEasternDateTimeLocal(instant: Date): string {
  const parts = partsAt(instant);
  return key(parts);
}

/** Formats report instants as NY civil time, never the viewer's browser zone. */
export function formatEasternDateTime(instant: Date): string {
  return new Intl.DateTimeFormat("en-US", { timeZone: EASTERN_TIME_ZONE, dateStyle: "medium", timeStyle: "short" }).format(instant);
}

/** Returns YYYY-MM-DD for an instant in the reporting business timezone. */
export function easternCivilDate(instant: Date): string {
  const parts = partsAt(instant);
  return `${parts.year}-${pad(parts.month)}-${pad(parts.day)}`;
}

/** Validates an actual Gregorian civil date rather than only its text shape. */
export function isValidEasternCivilDate(value: string): boolean {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return false;
  const year = Number(match[1]), month = Number(match[2]), day = Number(match[3]);
  const candidate = new Date(Date.UTC(year, month - 1, day));
  return candidate.getUTCFullYear() === year && candidate.getUTCMonth() === month - 1 && candidate.getUTCDate() === day;
}

/** Adds calendar dates without applying a fixed UTC offset or browser timezone. */
export function addEasternCivilDays(value: string, days: number): string {
  if (!isValidEasternCivilDate(value)) throw new Error(`Invalid Eastern civil date: ${value}`);
  const [year, month, day] = value.split("-").map(Number);
  const result = new Date(Date.UTC(year, month - 1, day + days));
  return `${result.getUTCFullYear()}-${pad(result.getUTCMonth() + 1)}-${pad(result.getUTCDate())}`;
}

export type EasternInstantParse = { ok: true; instant: Date } | { ok: false; error: string };

/**
 * Converts a native datetime-local selection to an explicit NY instant. It
 * rejects nonexistent spring-forward wall times and ambiguous fall-back wall
 * times rather than silently applying a browser-zone/default offset.
 */
export function easternDateTimeLocalToInstant(value: string): EasternInstantParse {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
  if (!match) return { ok: false, error: "Enter a complete America/New_York date and time." };
  const target: EasternParts = { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]), hour: Number(match[4]), minute: Number(match[5]), second: 0 };
  if (target.month < 1 || target.month > 12 || target.day < 1 || target.day > 31 || target.hour > 23 || target.minute > 59) return { ok: false, error: "Enter a valid America/New_York date and time." };
  const targetEpoch = Date.UTC(target.year, target.month - 1, target.day, target.hour, target.minute, 0);
  let millis = targetEpoch;
  for (let pass = 0; pass < 3; pass++) {
    const actual = partsAt(new Date(millis));
    millis += targetEpoch - Date.UTC(actual.year, actual.month - 1, actual.day, actual.hour, actual.minute, actual.second);
  }
  const instant = new Date(millis);
  if (key(partsAt(instant)) !== key(target)) return { ok: false, error: "That America/New_York wall time does not exist because of daylight-saving time." };
  const matches = [-3_600_000, 0, 3_600_000].filter(delta => key(partsAt(new Date(millis + delta))) === key(target));
  if (matches.length > 1) return { ok: false, error: "That America/New_York wall time is ambiguous because of daylight-saving time; choose a different time." };
  return { ok: true, instant };
}
